import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, ArrowRight, ArrowLeft, Loader2, AlertTriangle } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import AuthShell, { AuthHeading, FormAlert } from '../components/auth/AuthShell';
import AuthField from '../components/auth/AuthField';

const MIN_LEN = 8;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();

  // 'checking' | 'valid' | 'invalid'
  const [status, setStatus]     = useState('checking');
  const [email, setEmail]       = useState('');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors]     = useState({});
  const [formErr, setFormErr]   = useState(null);
  const pwRef = useRef(null);
  const confirmRef = useRef(null);

  // Validate the token on mount.
  useEffect(() => {
    let active = true;
    if (!token) { setStatus('invalid'); return undefined; }
    (async () => {
      try {
        const { data } = await api.get(`/auth/reset-password/${token}`);
        if (!active) return;
        if (data?.valid) {
          setEmail(data.email || '');
          setStatus('valid');
        } else {
          setStatus('invalid');
        }
      } catch {
        if (active) setStatus('invalid');
      }
    })();
    return () => { active = false; };
  }, [token]);

  // One rule set, used on blur and on submit.
  const check = (pw, cf) => {
    const e = {};
    if (!pw) e.password = 'Choose a new password.';
    else if (pw.length < MIN_LEN) e.password = `Use at least ${MIN_LEN} characters.`;
    if (!cf) e.confirm = 'Type the new password again.';
    else if (pw !== cf) e.confirm = "The passwords don't match.";
    return e;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setFormErr(null);
    const errs = check(password, confirm);
    setErrors(errs);
    if (errs.password) { pwRef.current?.focus(); return; }
    if (errs.confirm) { confirmRef.current?.focus(); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post('/auth/reset-password', { token, password });
      toast.success(data?.message || 'Password reset — you can now sign in.');
      navigate('/login', { replace: true });
    } catch (err) {
      const msg = err.response?.data?.message || '';
      if (!err.response) {
        setFormErr("We couldn't reach Rostery. Check your connection, then try again.");
      } else if (err.response.status === 400 && /link|expired|token|invalid/i.test(msg)) {
        // Only flip to the "invalid link" screen for genuine token errors — NOT for a
        // password-policy 400 (otherwise a too-short password looks like a dead link).
        setStatus('invalid');
      } else if (err.response.status === 400 && msg) {
        // Password policy (too common, too short…) — say it under the password.
        setErrors({ password: msg });
        pwRef.current?.focus();
      } else {
        setFormErr(msg || "We couldn't reset your password. Try again in a moment.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell>
      {/* ── Checking token ── */}
      {status === 'checking' && (
        <div role="status" className="flex items-center gap-2.5 py-8 text-sm text-slate-600">
          <Loader2 size={16} className="animate-spin text-slate-400" aria-hidden="true" />
          Checking your reset link…
        </div>
      )}

      {/* ── Invalid / expired ── */}
      {status === 'invalid' && (
        <div>
          <span className="w-11 h-11 rounded-xl bg-red-50 text-red-600 flex items-center justify-center mb-5" aria-hidden="true">
            <AlertTriangle size={20} />
          </span>
          <AuthHeading title="This link has expired">
            Reset links work once and only for a short time. Request a new one to continue.
          </AuthHeading>

          <Link to="/forgot-password" className="btn-primary w-full py-3 text-sm">
            Request a new link <ArrowRight size={15} aria-hidden="true" />
          </Link>

          <Link to="/login"
            className="flex items-center justify-center gap-1.5 min-h-[40px] text-sm text-slate-600 hover:text-slate-900 mt-3">
            <ArrowLeft size={15} aria-hidden="true" /> Back to sign in
          </Link>
        </div>
      )}

      {/* ── Valid: new-password form ── */}
      {status === 'valid' && (
        <div>
          <AuthHeading title="Choose a new password">
            {email ? <>For <strong className="font-semibold text-slate-700">{email}</strong>.</> : 'Choose a new password for your account.'}
          </AuthHeading>

          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <AuthField
              ref={pwRef}
              label="New password"
              icon={Lock}
              type="password"
              value={password}
              onChange={e => { setPassword(e.target.value); if (errors.password) setErrors(x => ({ ...x, password: null })); }}
              onBlur={() => { if (password) setErrors(x => ({ ...x, password: check(password, confirm).password || null })); }}
              placeholder="••••••••••"
              autoComplete="new-password"
              autoFocus
              hint={`At least ${MIN_LEN} characters.`}
              error={errors.password}
            />

            <AuthField
              ref={confirmRef}
              label="Confirm new password"
              icon={Lock}
              type="password"
              value={confirm}
              onChange={e => { setConfirm(e.target.value); if (errors.confirm) setErrors(x => ({ ...x, confirm: null })); }}
              onBlur={() => { if (confirm) setErrors(x => ({ ...x, confirm: check(password, confirm).confirm || null })); }}
              placeholder="••••••••••"
              autoComplete="new-password"
              error={errors.confirm}
            />

            {formErr && <FormAlert>{formErr}</FormAlert>}

            <button type="submit" disabled={submitting} className="btn-primary w-full py-3 text-sm">
              {submitting
                ? <><Loader2 size={16} className="animate-spin" aria-hidden="true" /> Resetting…</>
                : <>Reset password <ArrowRight size={15} aria-hidden="true" /></>}
            </button>
          </form>

          <Link to="/login"
            className="flex items-center justify-center gap-1.5 min-h-[40px] text-sm text-slate-600 hover:text-slate-900 mt-5">
            <ArrowLeft size={15} aria-hidden="true" /> Back to sign in
          </Link>
        </div>
      )}
    </AuthShell>
  );
}
